import { useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Button, Space, Spin, Typography } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import AgGrid from 'common/components/ag-grid/AgGrid';
import PreviewableModal from 'common/components/previewable-modal/PreviewableModal';
import {
  CustomNotification,
  dialogFunction,
  StyledModal,
  UploadFile,
} from 'common/components';
import * as productService from 'services/product';
import * as endpoints from 'services/product/endpoints';
import * as api from 'config/axios';
import { apiHandler } from 'utils/api';
import { useGetProductFullView } from 'hooks';
import { UPLOAD_MAX_SIZE } from 'static/Constants';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';

const SUPPORT_SDS_TYPES = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];

const baseLinkAssetsColumns = [
  {
    checkboxSelection: true,
    field: '',
    filter: false,
    resizable: false,
    suppressMenu: true,
    width: 50,
  },
  {
    dataType: 'string',
    displayName: 'Asset Name',
    fieldName: 'assetName',
    fieldNameCamelCase: 'assetName',
    flex: 1,
    minWidth: 200,
  },
  {
    dataType: 'string',
    displayName: 'Document Name',
    fieldName: 'documentName',
    fieldNameCamelCase: 'documentName',
  },
  {
    dataType: 'string',
    displayName: 'File Name',
    fieldName: 'fileName',
    fieldNameCamelCase: 'fileName',
    flex: 1,
    minWidth: 150,
  },
  {
    dataType: 'string',
    displayName: 'File Type',
    fieldName: 'fileType',
    fieldNameCamelCase: 'fileType',
  },
  {
    dataType: 'string',
    displayName: 'Status',
    fieldName: 'status',
    fieldNameCamelCase: 'status',
    flex: 1,
    minWidth: 100,
  },
];

const mapId = (item) => ({
  ...item,
  id: `${item?.linkId}`,
});

const SdsDataSheetModal = (props) => {
  const {
    formInstance,
    linkIdFieldName = 'SdsDataSheetsLinkId',
    onClose,
    onSubmit,
  } = props;

  const { id: productId } = useParams();
  const { productFull } = useGetProductFullView({ productId });

  const [activeLinkId, setActiveLinkId] = useState(
    formInstance.getFieldValue(linkIdFieldName),
  );
  const [rowsSelected, setRowSelected] = useState([]);
  const [reloadGrid, setReloadGrid] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const gridApiRef = useRef(null);

  const [itemSelected] = rowsSelected;

  const { linkPreview, extensionFile } = checkShowPdfPreview(
    previewItem?.pdfPreview,
    previewItem?.originalFile,
    previewItem?.fileType,
  );

  const linkAssetsColumns = [
    {
      displayName: 'Actions',
      suppressMenu: true,
      width: 100,
      cellRenderer: (params) => (
        <>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => setPreviewItem(params?.data)}
          />
          <Button
            type='link'
            icon={<DownloadOutlined />}
            onClick={() => {
              const downloadUrl = params?.data?.originalFile;

              if (!downloadUrl) {
                CustomNotification.error('Could not download file!');
                return;
              }
              api.sendDownload({ url: downloadUrl });
            }}
          />
        </>
      ),
    },
    ...baseLinkAssetsColumns,
  ];

  const onSetSelectedRows = (selectedNodesDetail) => {
    setRowSelected(selectedNodesDetail);
  };

  // The grid is remounted fresh every time this modal reopens, so its
  // internal selection state is lost even though activeLinkId still points
  // at the previously-linked file. Re-derive the selection from the fetched
  // rows so Submit/Download/Unlink and the "Selected file" label don't reset.
  const handleGetDataFromGrid = (gridData) => {
    if (!activeLinkId || rowsSelected.length > 0) return;

    const matchedItem = gridData?.find(
      (item) => `${item?.linkId}` === `${activeLinkId}`,
    );

    if (matchedItem) {
      setRowSelected([mapId(matchedItem)]);
    }
  };

  // Best-effort: also tick the grid's own checkbox once its rows exist.
  useEffect(() => {
    if (!itemSelected || !gridApiRef.current) return;

    const timeoutId = setTimeout(() => {
      gridApiRef.current.forEachNode((node) => {
        if (node.id === `${itemSelected.linkId}`) {
          node.setSelected(true);
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [itemSelected]);

  const downloadItem = () => {
    const downloadUrl = itemSelected?.originalFile;

    if (!downloadUrl) {
      CustomNotification.error('Could not download file');
      return;
    }
    api.sendDownload({ url: downloadUrl });
  };

  const unlinkItem = async () => {
    const params = {
      linkId: itemSelected.linkId,
      productItemId: productId,
      digitalAssetId: itemSelected.digitalAssetId,
    };

    await apiHandler({
      service: productService.unlinkProductDocument,
      params,
      successMessage: 'Unlink successfully!',
      errorMessage: 'Unlink fail!',
      failCallback: (message) => {
        CustomNotification.error(message);
      },
      successCallback: () => {
        setActiveLinkId(null);
        setRowSelected([]);
        formInstance.setFields([{ name: linkIdFieldName, value: null }]);
        onSubmit && onSubmit(null);
      },
    });
  };

  const showConfirmUnlinkModal = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to unlink this SDS sheet?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: () => unlinkItem(),
    });
  };

  const handleUploadResponse = async (response) => {
    const digitalAssetId = response?.data?.data?.assetId;

    if (!digitalAssetId) return;

    setIsLinking(true);

    const linkRes = await productService.linkAssetFromPIM(
      {
        digitalAssetId,
        productItemId: productId,
        productItemIds: null,
        subtype: 'General',
        associateType: 'DOCUMENT',
        linkType: 'DOCUMENT',
        documentName: response?.data?.data?.fileName,
      },
      'DOCUMENT',
    );

    if (linkRes?.isSuccess) {
      CustomNotification.success('SDS sheet uploaded successfully!');
      setActiveLinkId(linkRes?.data?.linkId);
      setReloadGrid((prev) => !prev);
      setIsUploadModalOpen(false);
    } else {
      CustomNotification.error(linkRes?.message || 'Failed to link file');
    }

    setIsLinking(false);
  };

  const handleSubmit = () => {
    formInstance.setFields([
      { name: linkIdFieldName, value: itemSelected?.linkId },
    ]);
    onSubmit && onSubmit(itemSelected);
    onClose();
  };

  return (
    <StyledModal
      title='SDS Data Sheet'
      visible
      onCancel={onClose}
      width='80vw'
      maskClosable={false}
      destroyOnClose
      footer={
        <Button
          type='primary'
          disabled={!itemSelected}
          loading={isLinking}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      }
    >
      <Space style={{ marginBottom: 12 }}>
        <Button disabled={!itemSelected} onClick={downloadItem}>
          Download
        </Button>
        <Button disabled={!itemSelected} onClick={showConfirmUnlinkModal}>
          Unlink
        </Button>
        <Button
          icon={<UploadOutlined />}
          onClick={() => setIsUploadModalOpen(true)}
        >
          Upload
        </Button>
      </Space>

      {isUploadModalOpen && (
        <StyledModal
          title='Upload SDS Sheet'
          visible
          onCancel={() => setIsUploadModalOpen(false)}
          maskClosable={false}
          destroyOnClose
          footer={null}
        >
          <Spin spinning={isLinking} tip='Linking file to product...'>
            <UploadFile
              showList
              showMultiple={false}
              supportTypes={SUPPORT_SDS_TYPES}
              maxSize={UPLOAD_MAX_SIZE.GENERAL}
              apiUrl={endpoints.UPLOAD_PIM_ASSET}
              additionalBodyPayload={() => ({
                assetType: 'Document',
                assetSubType: 'General',
                ownerId: productFull?.ownerId,
                productId,
              })}
              getResponse={handleUploadResponse}
              showUploadSuccessMsg={false}
            />
          </Spin>
        </StyledModal>
      )}

      <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
        Selected file:{' '}
        <Typography.Text strong>
          {itemSelected?.fileName || 'None'}
        </Typography.Text>
      </Typography.Text>

      <div style={{ height: 400 }}>
        <AgGrid
          columnDefs={linkAssetsColumns}
          urlGrid={endpoints.GET_PRODUCT_DOCUMENTS_ENDPOINT}
          responseParams='data'
          requestParams={{ productId }}
          onHandleSelectedRowCustom={onSetSelectedRows}
          reloadGrid={reloadGrid}
          mapId={mapId}
          gridConfigProps={{ rowSelection: 'single' }}
          getDataFromGrid={handleGetDataFromGrid}
          getGridApi={(gridApi) => (gridApiRef.current = gridApi)}
        />
      </div>

      {previewItem && (
        <PreviewableModal
          id={previewItem?.linkId}
          link={linkPreview}
          title={previewItem?.fileName}
          visible={Boolean(previewItem)}
          fileName={previewItem?.fileName}
          cancelModal={() => setPreviewItem(null)}
          downloadLink={previewItem?.originalFile}
          isDownload={false}
          fileType={extensionFile}
          fileSize={previewItem?.fileSize}
          isDocument={true}
        />
      )}
    </StyledModal>
  );
};

export default SdsDataSheetModal;
